import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../Api/baseQueryWithReauth';
import type {
  AccountingExportDocumentItem,
  AccountingExportFileQueryWithFilename,
  AccountingExportItemsQuery,
} from '../Types/accountingExportTypes';
import {
  ACCOUNTING_EXPORT_TAG,
  buildAccountingExportItemsRequest,
  buildAccountingPurchasesEdiExportRequest,
  buildAccountingPurchasesNexoXmlExportRequest,
  buildAccountingSalesEdiExportRequest,
  buildAccountingSalesJpkFa4ExportRequest,
  buildAccountingSalesNexoXmlExportRequest,
  getAccountingExportItemsInvalidatesTags,
  getAccountingExportItemsProvidesTags,
  mapAccountingExportItemsResponse,
  triggerBlobDownload,
} from './accountingExportApi.contract';

export type AccountingExportFileResult = { downloaded: boolean };

export const accountingExportsApi = createApi({
  reducerPath: 'accountingExportsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [ACCOUNTING_EXPORT_TAG],
  endpoints: (builder) => ({
    getAccountingExportItems: builder.query<AccountingExportDocumentItem[], AccountingExportItemsQuery>({
      query: buildAccountingExportItemsRequest,
      transformResponse: mapAccountingExportItemsResponse,
      providesTags: getAccountingExportItemsProvidesTags,
    }),
    exportAccountingSalesJpkFa4: builder.mutation<AccountingExportFileResult, AccountingExportFileQueryWithFilename>({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery(buildAccountingSalesJpkFa4ExportRequest(arg));
        if (result.error) {
          return { error: result.error };
        }
        triggerBlobDownload(result.data as Blob, arg.filename);
        return { data: { downloaded: true } };
      },
      invalidatesTags: getAccountingExportItemsInvalidatesTags,
    }),
    exportAccountingSalesEdi: builder.mutation<AccountingExportFileResult, AccountingExportFileQueryWithFilename>({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery(buildAccountingSalesEdiExportRequest(arg));
        if (result.error) {
          return { error: result.error };
        }
        triggerBlobDownload(result.data as Blob, arg.filename);
        return { data: { downloaded: true } };
      },
      invalidatesTags: getAccountingExportItemsInvalidatesTags,
    }),
    exportAccountingPurchasesEdi: builder.mutation<AccountingExportFileResult, AccountingExportFileQueryWithFilename>({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery(buildAccountingPurchasesEdiExportRequest(arg));
        if (result.error) {
          return { error: result.error };
        }
        triggerBlobDownload(result.data as Blob, arg.filename);
        return { data: { downloaded: true } };
      },
      invalidatesTags: getAccountingExportItemsInvalidatesTags,
    }),
    exportAccountingSalesNexoXml: builder.mutation<AccountingExportFileResult, AccountingExportFileQueryWithFilename>({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery(buildAccountingSalesNexoXmlExportRequest(arg));
        if (result.error) {
          return { error: result.error };
        }
        triggerBlobDownload(result.data as Blob, arg.filename);
        return { data: { downloaded: true } };
      },
      invalidatesTags: getAccountingExportItemsInvalidatesTags,
    }),
    exportAccountingPurchasesNexoXml: builder.mutation<AccountingExportFileResult, AccountingExportFileQueryWithFilename>({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery(buildAccountingPurchasesNexoXmlExportRequest(arg));
        if (result.error) {
          return { error: result.error };
        }
        triggerBlobDownload(result.data as Blob, arg.filename);
        return { data: { downloaded: true } };
      },
      invalidatesTags: getAccountingExportItemsInvalidatesTags,
    }),
  }),
});

export const {
  useGetAccountingExportItemsQuery,
  useExportAccountingSalesJpkFa4Mutation,
  useExportAccountingSalesEdiMutation,
  useExportAccountingPurchasesEdiMutation,
  useExportAccountingSalesNexoXmlMutation,
  useExportAccountingPurchasesNexoXmlMutation,
} = accountingExportsApi;

